import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const YesPartialNoRadio = ({ name, value, onChange, className = '' }) => {
  const options = [
    { value: 'yes', label: 'Yes', color: 'text-green-600' },
    { value: 'partial', label: 'Partial', color: 'text-yellow-600' },
    { value: 'no', label: 'No', color: 'text-red-600' },
  ];

  return (
    <div className={`flex gap-4 ${className}`}>
      {options.map(option => (
        <label key={option.value} className="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={e => onChange(e.target.value)}
            className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-blue-500"
          />
          <span className={`text-sm font-medium ${option.color}`}>{option.label}</span>
        </label>
      ))}
    </div>
  );
};

const EntrepreneurProfilingAssessment = ({ data = {}, onSave, userRole }) => {
  const [assessmentData, setAssessmentData] = useState({
    entrepreneurProfiling: {
      personalityAssessment: data.entrepreneurProfiling?.personalityAssessment || 'no',
      mindsetAssessment: data.entrepreneurProfiling?.mindsetAssessment || 'no',
      capabilitiesAssessment: data.entrepreneurProfiling?.capabilitiesAssessment || 'no',
      structuredTool: data.entrepreneurProfiling?.structuredTool || 'no',
      notes: data.entrepreneurProfiling?.notes || '',
    },
    businessProfiling: {
      businessStage: data.businessProfiling?.businessStage || 'no',
      businessModel: data.businessProfiling?.businessModel || 'no',
      industryAssessment: data.businessProfiling?.industryAssessment || 'no',
      scalabilityReadiness: data.businessProfiling?.scalabilityReadiness || 'no',
      notes: data.businessProfiling?.notes || '',
    },
    needsDiagnosis: {
      baselineDiagnostics: data.needsDiagnosis?.baselineDiagnostics || 'no',
      personalizedPath: data.needsDiagnosis?.personalizedPath || 'no',
      ongoingAssessment: data.needsDiagnosis?.ongoingAssessment || 'no',
      notes: data.needsDiagnosis?.notes || '',
    },
    score: data.score || 0,
    notes: data.notes || '',
  });

  const [saving, setSaving] = useState(false);

  const calculateScore = () => {
    let totalPoints = 0;
    let maxPoints = 0;

    const getPoints = value => {
      if (value === 'yes') return 1;
      if (value === 'partial') return 0.5;
      return 0;
    };

    const entrepreneurProfiling = assessmentData.entrepreneurProfiling;
    totalPoints += getPoints(entrepreneurProfiling.personalityAssessment);
    totalPoints += getPoints(entrepreneurProfiling.mindsetAssessment);
    totalPoints += getPoints(entrepreneurProfiling.capabilitiesAssessment);
    totalPoints += getPoints(entrepreneurProfiling.structuredTool);
    maxPoints += 4;

    const businessProfiling = assessmentData.businessProfiling;
    totalPoints += getPoints(businessProfiling.businessStage);
    totalPoints += getPoints(businessProfiling.businessModel);
    totalPoints += getPoints(businessProfiling.industryAssessment);
    totalPoints += getPoints(businessProfiling.scalabilityReadiness);
    maxPoints += 4;

    const needsDiagnosis = assessmentData.needsDiagnosis;
    totalPoints += getPoints(needsDiagnosis.baselineDiagnostics);
    totalPoints += getPoints(needsDiagnosis.personalizedPath);
    totalPoints += getPoints(needsDiagnosis.ongoingAssessment);
    maxPoints += 3;

    return Math.round((totalPoints / maxPoints) * 100);
  };

  const handleSave = async () => {
    setSaving(true);
    const updatedData = {
      ...assessmentData,
      score: calculateScore(),
      lastUpdated: new Date().toISOString(),
    };

    try {
      await onSave(updatedData);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const getScoreColor = score => {
    if (score >= 81) return 'text-green-600';
    if (score >= 61) return 'text-yellow-600';
    if (score >= 41) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreStatus = score => {
    if (score >= 81) return 'Healthy & Scalable';
    if (score >= 61) return 'Emerging Maturity';
    if (score >= 41) return 'At Risk';
    return 'Critical Gaps';
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-green-200 bg-green-50 p-6">
        <div className="mb-4 flex items-center gap-3">
          <UserGroupIcon className="h-8 w-8 text-green-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Entrepreneur Profiling & Assessment
            </h2>
            <p className="text-gray-600">
              Evaluate the effectiveness of entrepreneur and business profiling tools and assessment
              processes
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600">Current Score</div>
            <div className={`text-3xl font-bold ${getScoreColor(calculateScore())}`}>
              {calculateScore()}%
            </div>
            <div className="text-sm text-gray-600">{getScoreStatus(calculateScore())}</div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-green-600 px-6 py-3 font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Assessment'}
          </button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <UserGroupIcon className="h-6 w-6 text-blue-600" />
          <h3 className="text-xl font-semibold text-gray-900">Entrepreneur Profiling Tools</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program apply personality assessment tools?
            </label>
            <p className="text-xs text-gray-600">
              Use of validated personality assessments to understand entrepreneur traits and
              characteristics
            </p>
            <YesPartialNoRadio
              name="personalityAssessment"
              value={assessmentData.entrepreneurProfiling.personalityAssessment}
              onChange={value =>
                updateField('entrepreneurProfiling', 'personalityAssessment', value)
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program assess entrepreneurial mindset?
            </label>
            <p className="text-xs text-gray-600">
              Evaluation of entrepreneurial attitudes, beliefs, and cognitive patterns
            </p>
            <YesPartialNoRadio
              name="mindsetAssessment"
              value={assessmentData.entrepreneurProfiling.mindsetAssessment}
              onChange={value => updateField('entrepreneurProfiling', 'mindsetAssessment', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program assess entrepreneurial capabilities?
            </label>
            <p className="text-xs text-gray-600">
              Evaluation of skills, knowledge, and competencies relevant to entrepreneurship
            </p>
            <YesPartialNoRadio
              name="capabilitiesAssessment"
              value={assessmentData.entrepreneurProfiling.capabilitiesAssessment}
              onChange={value =>
                updateField('entrepreneurProfiling', 'capabilitiesAssessment', value)
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is a structured entrepreneur profiling tool used?
            </label>
            <p className="text-xs text-gray-600">
              Systematic approach to capturing and documenting entrepreneur characteristics
            </p>
            <YesPartialNoRadio
              name="structuredTool"
              value={assessmentData.entrepreneurProfiling.structuredTool}
              onChange={value => updateField('entrepreneurProfiling', 'structuredTool', value)}
            />
          </div>

          <div>
            <label
              htmlFor="entrepreneurProfilingNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="entrepreneurProfilingNotes"
              value={assessmentData.entrepreneurProfiling.notes}
              onChange={e => updateField('entrepreneurProfiling', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <ClipboardDocumentListIcon className="h-6 w-6 text-purple-600" />
          <h3 className="text-xl font-semibold text-gray-900">Business Profiling Tools</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program assess business stage?
            </label>
            <p className="text-xs text-gray-600">
              Evaluation of where the business is in its lifecycle (startup, growth, maturity, etc.)
            </p>
            <YesPartialNoRadio
              name="businessStage"
              value={assessmentData.businessProfiling.businessStage}
              onChange={value => updateField('businessProfiling', 'businessStage', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program assess business model?
            </label>
            <p className="text-xs text-gray-600">
              Evaluation of how the business creates, delivers, and captures value
            </p>
            <YesPartialNoRadio
              name="businessModel"
              value={assessmentData.businessProfiling.businessModel}
              onChange={value => updateField('businessProfiling', 'businessModel', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program assess industry context?
            </label>
            <p className="text-xs text-gray-600">
              Understanding of industry dynamics, competition, and market conditions
            </p>
            <YesPartialNoRadio
              name="industryAssessment"
              value={assessmentData.businessProfiling.industryAssessment}
              onChange={value => updateField('businessProfiling', 'industryAssessment', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program assess scalability readiness?
            </label>
            <p className="text-xs text-gray-600">
              Evaluation of the business's potential and readiness for growth and expansion
            </p>
            <YesPartialNoRadio
              name="scalabilityReadiness"
              value={assessmentData.businessProfiling.scalabilityReadiness}
              onChange={value => updateField('businessProfiling', 'scalabilityReadiness', value)}
            />
          </div>

          <div>
            <label
              htmlFor="businessProfilingNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="businessProfilingNotes"
              value={assessmentData.businessProfiling.notes}
              onChange={e => updateField('businessProfiling', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <MagnifyingGlassIcon className="h-6 w-6 text-orange-600" />
          <h3 className="text-xl font-semibold text-gray-900">Needs Diagnosis</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are baseline diagnostics used to define a personalized development path?
            </label>
            <p className="text-xs text-gray-600">
              Initial assessment that informs tailored support and development planning
            </p>
            <YesPartialNoRadio
              name="baselineDiagnostics"
              value={assessmentData.needsDiagnosis.baselineDiagnostics}
              onChange={value => updateField('needsDiagnosis', 'baselineDiagnostics', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is there a system for creating personalized development paths?
            </label>
            <p className="text-xs text-gray-600">
              Structured approach to customizing program content and support based on individual
              needs
            </p>
            <YesPartialNoRadio
              name="personalizedPath"
              value={assessmentData.needsDiagnosis.personalizedPath}
              onChange={value => updateField('needsDiagnosis', 'personalizedPath', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are there ongoing assessment mechanisms to track progress?
            </label>
            <p className="text-xs text-gray-600">
              Regular evaluation and adjustment of development plans based on progress and changing
              needs
            </p>
            <YesPartialNoRadio
              name="ongoingAssessment"
              value={assessmentData.needsDiagnosis.ongoingAssessment}
              onChange={value => updateField('needsDiagnosis', 'ongoingAssessment', value)}
            />
          </div>

          <div>
            <label
              htmlFor="needsDiagnosisNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="needsDiagnosisNotes"
              value={assessmentData.needsDiagnosis.notes}
              onChange={e => updateField('needsDiagnosis', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-orange-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <h3 className="mb-4 text-xl font-semibold text-gray-900">Overall Assessment Notes</h3>
        <textarea
          value={assessmentData.notes}
          onChange={e => setAssessmentData(prev => ({ ...prev, notes: e.target.value }))}
          rows={4}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
          placeholder="Add any overall observations, recommendations, or strategic insights..."
        />
      </motion.div>
    </div>
  );
};

export default EntrepreneurProfilingAssessment;
